/**
 * @file Файл содержит код для настройки проекта и отправки его на сервер.
 */

import 'setimmediate';

import lodash from 'lodash';

import { makeAutoObservable, runInAction } from 'mobx';

import AppStore from 'AppStore';
import messageBoxStore from 'MessageBox/MessageBoxStore';

import client, { ApiError } from 'client';

import { MARKET_MODES } from 'market';

import { fetchPriceWithCache, readPriceFromCache } from './priceCache';

/**
 * Минимальный интервал между запросами цены.
 *
 * Ползунок шлёт событие на каждом делении: протащить его от края до края на
 * Авито — это 247 событий, а на /api стоит лимит 120 запросов в минуту (2 в
 * секунду, burst 50, ключ — IP, так что офис за одним NAT делит квоту).
 *
 * Первый запрос уходит сразу, дальше не чаще одного за интервал, и последний
 * — обязательно: цена обновляется прямо во время перетаскивания и в конце
 * всегда соответствует тому, что видно на экране. Чистая пауза (debounce)
 * этого не даёт — при непрерывном движении цена бы просто застыла.
 */
const PRICE_REQUEST_MIN_INTERVAL_MS = 500;

/**
 * Класс для настройки действий в проекте.
 * @property {number} sliderValue Текущее значение бегунка.
 * @property {number} sliderValueMin Минимальное значение бегунка.
 * @property {number} sliderValueMax Максимальное значение бегунка.
 */
class Calculator {

  store;
  market;
  mode;

  constructor(store, market, mode) {
    this.store = store;
    this.market = market;
    this.mode = mode;

    const modeId = mode.id;

    this.isChains = modeId === MARKET_MODES.CHAINS;
    this.isReviews = modeId === MARKET_MODES.REVIEWS;
    this.isViews = modeId === MARKET_MODES.VIEWS;

    this.reviewsMin = this.isReviews ? 1 : 0;
    this.chainsPerReview = 4;

    // Денег здесь нет вообще: и цена, и границы «от … до», и прайс для
    // подписей приезжают ответом /projects/price. До первого ответа — нули.
    // Калькулятор считает только количества: границы ползунка, шаги и
    // распределение действий по цепочкам.
    this.price = 0;
    this.priceMin = 0;
    this.priceMax = 0;
    this.serverPrice = 0;
    this.pricing = null;
    this.isPriceLoading = false;
    this.priceError = false;
    this._priceRequestSeq = 0;
    this._priceTimer = null;
    this._priceLastRequestAt = 0;

    this.chainsMin = mode.bots ? (store.isBotFarm ? 12000 : 4000) : Math.max(this.isChains ? market.chainsMin : 0, this.reviewsMin * this.chainsPerReview);
    this.chainsMax = mode.bots ? 80000 : market.chainsMax;
    if (this.isChains) {
      this.sliderValueMin = this.chainsMin / (mode.chainsStep || market.chainsStep);
      this.sliderValueMax = this.chainsMax / (mode.chainsStep || market.chainsStep);
    }

    this.viewsMin = this.isViews
      ? ((market.isMusicPlatform ? 1 : mode.actions.filter(
        action => action.type.startsWith('VIEW.')
      ).length) * market.viewsStep)
      : 0;
    this.viewsMax = market.viewsMax ?? 300;

    this.viewsPerDay = market.viewsPerDay ?? 90;
    if (this.isViews) {
      this.sliderValueMin = this.viewsMin / market.viewsStep;
      this.sliderValueMax = this.viewsMax / market.viewsStep;
    }

    this.reviewsMax = 6;
    this.reviewsPerDay = 0.5;
    this.reviewsOffsetDays = 3;
    if (this.isReviews) {
      this.sliderValueMin = this.reviewsMin;
      this.sliderValueMax = this.reviewsMax;
    }

    this.actions = this.mode.actions.map(action => ({
      ...action,
      share: action.initialShare ?? 0.0,
      count: 0,
    }));

    this.actionsPerChain = this.actions.reduce((acc, action) => acc + (action.type.startsWith('ALWAYS.') ? 1 : 0), 0)
      + (this.actions.find(action => action.type.startsWith('MAIN.') && action.type !== 'MAIN.REVIEW') ? 1 : 0)
      + (this.actions.find(action => action.type.startsWith('EXTRA.')) ? 1 : 0);

    this.reviewsPerDayNormal = 0.5;

    this.deadlines = mode.bots ? [
      {
        emoji: '👁',
        title: 'Протестировать',
        text: 'Демонстрация возможностей',
        chainsPerDay: 84,
        reviewsPerDay: 1,
        days: mode.bots ? 1 : undefined,
      },
      {
        emoji: '🚀',
        title: 'Увидеть результат',
        text: 'Максимум результата',
        chainsPerDay: 42,
        reviewsPerDay: 0.5,
        days: mode.bots ? 30 : undefined,
      },
    ] : [
      {
        emoji: '🚀',
        title: 'Быстрее',
        text: 'Нет времени ждать. Полетели!',
        chainsPerDay: this.market.chainsPerDayNormal * 2,
        reviewsPerDay: this.reviewsPerDayNormal * 2,
        days: undefined,
      },
      {
        emoji: '☯️',
        title: 'Оптимально',
        text: 'Золотая середина и баланс в темпе',
        chainsPerDay: this.market.chainsPerDayNormal,
        reviewsPerDay: this.reviewsPerDayNormal,
        days: undefined,
      },
      {
        emoji: '🐌',
        title: 'Медленнее',
        text: 'Ползем и никуда не торопимся',
        chainsPerDay: this.market.chainsPerDayNormal / 2,
        reviewsPerDay: this.reviewsPerDayNormal / 1.5,
        days: undefined,
      },
    ];

    this.selectedDeadlineIndex = 1;
    this.testing = false;

    this.setChainsCount(lodash.clamp(this.isChains ? market.chainsDefault : (this.isReviews ? 4 : 0), this.chainsMin, this.chainsMax), false);

    this.setViewsCount(lodash.clamp(this.isViews
      ? ((market.isMusicPlatform ? 1 : mode.actions.filter(
        action => action.type.startsWith('VIEW.')
      ).length) * this.market.viewsStep) : 0, this.viewsMin, this.viewsMax), false);

    this.setReviewsCount(lodash.clamp(this.isReviews ? 1 : 0, this.reviewsMin, this.reviewsMax), false);

    this.recalculateCounters();
    this.recalculatePriceActionsDays();
  }

  recalculateBotsPricing() {
    const { store, market, mode } = this;
    if (!mode.bots)
      return;

    this.chainsMin = store.isBotFarm ? 12000 : 4000;
    this.sliderValueMin = this.chainsMin / (mode.chainsStep || market.chainsStep);

    if (this.chainsCount < this.chainsMin)
      this.setChainsCount(this.chainsMin, false);
    else if (this.chainsCount > this.chainsMax)
      this.setChainsCount(this.chainsMax, false);

    this.recalculateCounters();
    // Пересчитываем всегда: смена сервера меняет цену, даже когда количество
    // ботов осталось прежним.
    this.recalculatePriceActionsDays();
  }

  onSetBotIsFarm() {
    this.recalculateBotsPricing();
  }

  setActionCount(action, count) {
    if (count < 0)
      return;
    action.count = count;

    const type = action.type;

    action.canAdd = (() => {
      if (type === 'MAIN.REVIEW')
        return this.reviewsCount < this.reviewsMax;
      if (type.startsWith('VIEW.'))
        return this.viewsCount < this.viewsMax;
      return this.chainsCount < this.chainsMax;
    })();

    action.canSub = (() => {
      if (!count)
        return false;

      if (type === 'MAIN.REVIEW')
        return this.reviewsCount > this.reviewsMin;
      if (type.startsWith('VIEW.'))
        return this.viewsCount > this.viewsMin;
      return this.chainsCount > this.chainsMin;
    })();
  }

  /**
   * Устанавливает значение бегунка.
   * @param {number|string} value 
   * @returns {void}
   */
  setSliderValue(value) {
    if (typeof value !== 'number')
      value = Number(value);
    if (value < this.sliderValueMin || value > this.sliderValueMax)
      return;

    if (this.isChains)
      return this.setChainsCount(value * (this.mode.chainsStep || this.market.chainsStep));
    if (this.isReviews)
      return this.setReviewsCount(value);
    if (this.isViews)
      return this.setViewsCount(value * this.market.viewsStep);
  }

  setChainsCount(count, recalculate = true) {
    if (count < this.chainsMin || count > this.chainsMax)
      return;
    this.chainsCount = count;
    if (this.isChains)
      this.sliderValue = count / (this.mode.chainsStep || this.market.chainsStep);

    this.reviewsCount = Math.min(this.reviewsCount, Math.floor(count / this.chainsPerReview));

    if (recalculate) {
      this.recalculateCounters();
      this.recalculatePriceActionsDays();
    }
  }

  setReviewsCount(count, recalculate = true) {
    if (count < this.reviewsMin || count > this.reviewsMax)
      return;
    this.reviewsCount = count;
    if (this.isReviews)
      this.sliderValue = count;

    const newChainsCount = Math.ceil(count * this.chainsPerReview);
    this.setChainsCount(newChainsCount, false);

    if (recalculate) {
      this.recalculateCounters();
      this.recalculatePriceActionsDays();
    }
  }

  setViewsCount(count, recalculate = true) {
    if (count < this.viewsMin || count > this.viewsMax)
      return;
    this.viewsCount = count;
    if (this.isViews)
      this.sliderValue = count / this.market.viewsStep;

    if (recalculate) {
      this.recalculateCounters();
      this.recalculatePriceActionsDays();
    }
  }

  recalculateCounters(categoryToExclude) {
    const market = this.market;
    const mode = this.mode;

    const chainsStep = mode.chainsStep || market.chainsStep;

    if (categoryToExclude !== 'MAIN') {
      this.fillCountersByTheirDistribution('MAIN', this.chainsCount / chainsStep);
      this.getActionsStartsWith('MAIN').forEach(action => { action.count *= chainsStep });
    }
    this.fillCountersByConstant('ALWAYS', this.chainsCount / chainsStep);
    this.getActionsStartsWith('ALWAYS').forEach(action => { action.count *= chainsStep });
    if (categoryToExclude !== 'EXTRA') {
      this.fillCountersByTheirDistribution('EXTRA', this.chainsCount / chainsStep);
      this.getActionsStartsWith('EXTRA').forEach(action => { action.count *= chainsStep });
    }

    this.fillCountersByTheirDistribution('VIEW', this.viewsCount / market.viewsStep);
    this.getActionsStartsWith('VIEW').forEach(action => { action.count *= market.viewsStep });

    const reviewAction = this.actions.find(action => action.type === 'MAIN.REVIEW');
    if (reviewAction)
      reviewAction.count = this.reviewsCount;

    this.actions.forEach(action => { this.setActionCount(action, action.count) });
  }

  getActionsStartsWith(startsWith) {
    return this.actions.filter(action => action.type.startsWith(startsWith));
  }

  fillCountersByConstant(startsWith, value) {
    const actions = this.getActionsStartsWith(startsWith).filter(action => action.type !== 'MAIN.REVIEW');
    actions.forEach(action => { this.setActionCount(action, value) });
  }

  fillCountersByTheirDistribution(startsWith, chains, actionToExclude = null) {
    const actions = this.getActionsStartsWith(startsWith).filter(action => action.type !== 'MAIN.REVIEW')
      .filter(action => action !== actionToExclude);
    actions.forEach(action => { this.setActionCount(action, Math.round(chains * action.share)) });

    const total = actions.reduce((acc, action) => acc + action.count, 0);

    let diff = chains - total;
    while (diff > 0) {
      const min = actions.reduce((acc, action) => !action.count || (acc.count < action.count) ? acc : action, actions[0]);
      this.setActionCount(min, min.count + 1);
      diff--;
    }
    while (diff < 0) {
      const max = actions.reduce((acc, action) => !action.count || (acc.count > action.count) ? acc : action, actions[0]);
      this.setActionCount(max, max.count - 1);
      diff++;
    }
  }

  addActionCount(action, add) {
    const countNew = action.count + add;
    if (countNew < 0)
      return;

    const type = action.type;
    if (type.startsWith('VIEW')) {
      const viewsCountNew = this.viewsCount + add;
      if (viewsCountNew < this.viewsMin || viewsCountNew > this.viewsMax)
        return;
      this.viewsCount = viewsCountNew;
      if (this.isViews)
        this.sliderValue = this.viewsCount / this.market.viewsStep;

      this.setActionCount(action, countNew);
      this.actions.forEach(action => { this.setActionCount(action, action.count) });

      this.recalculateDistribution('VIEW');
      this.recalculatePriceActionsDays();
      return;
    }

    let chainsCountNew = this.chainsCount;

    if (type === 'MAIN.REVIEW') {
      if (countNew < this.reviewsMin || countNew > this.reviewsMax)
        return;
      if (this.chainsCount < Math.ceil(countNew * this.chainsPerReview)) {
        chainsCountNew = Math.ceil(countNew * this.chainsPerReview);
        if (chainsCountNew > this.chainsMax)
          return;
      }
      this.setActionCount(action, countNew);
      this.reviewsCount = countNew;
      if (this.isReviews)
        this.sliderValue = this.reviewsCount;
    } else {
      chainsCountNew += add;
    }

    if (chainsCountNew !== this.chainsCount) {
      if (type === 'MAIN.REVIEW') {
        this.setChainsCount(chainsCountNew);
        return;
      }

      if (chainsCountNew < this.chainsMin || chainsCountNew > this.chainsMax)
        return;
      this.chainsCount = chainsCountNew;
      if (this.isChains)
        this.sliderValue = chainsCountNew / (this.mode.chainsStep || this.market.chainsStep);

      if (this.reviewsCount) {
        this.reviewsCount = Math.min(Math.floor(this.chainsCount / this.chainsPerReview), this.reviewsCount);
        this.setActionCount(this.actions.find(action => action.type === 'MAIN.REVIEW'), this.reviewsCount);
        if (this.isReviews)
          this.sliderValue = this.reviewsCount;
      }

      const category = type.substring(0, type.indexOf('.'));
      if (category !== 'ALWAYS')
        this.setActionCount(action, countNew);
      this.recalculateCounters(category);
      if (category !== 'ALWAYS')
        this.recalculateDistribution(category);
    }

    this.actions.forEach(action => { this.setActionCount(action, action.count) });
    this.recalculatePriceActionsDays();
  }

  recalculateDistribution(startsWith) {
    const actions = this.getActionsStartsWith(startsWith).filter(action => action.type !== 'MAIN.REVIEW');
    const total = actions.reduce((acc, action) => acc + action.count, 0);
    if (!total)
      return;
    actions.forEach(action => { action.share = action.count / total });
  }

  /**
   * Увеличить количество действия.
   */
  addAction(action) {
    const market = this.market;
    const mode = this.mode;
    this.store.setIsActivitiesCalculatorManual(true);
    this.addActionCount(action, 0 + (action.type.startsWith('VIEW.') ? market.viewsStep : (mode.chainsStep || market.chainsStep)));
  }
  /**
   * Уменьшить количество действия.
   */
  subAction(action) {
    const market = this.market;
    const mode = this.mode;
    this.store.setIsActivitiesCalculatorManual(true);
    this.addActionCount(action, 0 - (action.type.startsWith('VIEW.') ? market.viewsStep : (mode.chainsStep || market.chainsStep)));
  }

  recalculatePriceActionsDays() {
    const reviewsCount = this.actions.find(action => action.type === 'MAIN.REVIEW')?.count ?? 0;
    this.reviewsCount = reviewsCount;

    const viewsTotalCount = this.actions.filter(action => action.type.startsWith('VIEW.')).reduce((acc, action) => acc + action.count, 0);
    this.viewsCount = viewsTotalCount;

    this.actionsCount = this.actions.reduce((acc, action) => acc + ((action.subactions ?? 1) * action.count), 0);

    if (!this.mode.bots) {
      const deadlines = this.deadlines;
      for (const deadline of deadlines) {
        deadline.days = Math.round(Math.max(
          this.chainsCount / deadline.chainsPerDay,
          reviewsCount / deadline.reviewsPerDay + (reviewsCount ? this.reviewsOffsetDays : 0),
          viewsTotalCount / this.viewsPerDay
        ));
      }
      // fix deadline errors
      deadlines[0].days = Math.max(deadlines[0].days, 1);
      for (let i = 1; i < deadlines.length; ++i)
        deadlines[i].days = Math.max(deadlines[i].days, deadlines[i - 1].days + 1);
    }

    // Цену и границы показываем только серверные: тот же прайс и та же
    // формула, по которым цена будет списана.
    this.syncServerPrice();
  }

  /**
   * Состав проекта в том виде, в каком его считает сервер: без действий с
   * нулевым счётчиком и с отметкой гарантии на отзыв.
   */
  buildActivities() {
    return this.actions
      .filter(action => action.count > 0)
      .map(action => (action.type === 'MAIN.REVIEW' && this.store.reviewWarranty)
        ? { type: action.type, count: action.count, warranty: true }
        : { type: action.type, count: action.count });
  }

  /**
   * Края ползунка в штуках: что именно он двигает и в каких пределах. Во что
   * это превращается в рублях, решает сервер.
   */
  get sliderRange() {
    if (this.isReviews)
      return { axis: 'reviews', min: this.reviewsMin, max: this.reviewsMax };
    if (this.isViews)
      return { axis: 'views', min: this.viewsMin, max: this.viewsMax };
    return { axis: 'chains', min: this.chainsMin, max: this.chainsMax };
  }

  /** Признак общего сервера для ботов: персональная ферма — только по выбору. */
  get useCommunalServer() {
    return this.mode.bots && (this.store.testing || !this.store.isBotFarm);
  }

  /**
   * Спросить цену у сервера. Ответы приходят не в порядке отправки, поэтому
   * применяется только последний запрошенный — иначе цена «прыгает» назад на
   * запоздавшем ответе от предыдущего положения ползунка.
   */
  syncServerPrice() {
    const payload = this.buildPricePayload();
    if (!payload)
      return;

    // Уже посчитанное отдаём сразу и без запроса: возврат ползунка на прежнее
    // деление не должен мигать «считаем».
    const cached = readPriceFromCache(payload);
    if (cached) {
      this._priceRequestSeq++;
      this.clearPriceTimer();
      this.applyServerPricing(cached);
      this.isPriceLoading = false;
      this.priceError = false;
      return;
    }

    this.isPriceLoading = true;
    this.priceError = false;
    this.clearPriceTimer();

    // Первый запрос — сразу: на открытии страницы цена не должна ждать паузу.
    const wait = Math.max(
      0,
      PRICE_REQUEST_MIN_INTERVAL_MS - (Date.now() - this._priceLastRequestAt)
    );
    if (!wait) {
      this.requestServerPrice(payload);
      return;
    }

    this._priceTimer = setTimeout(
      () => runInAction(() => this.requestServerPrice(payload)),
      wait
    );
  }

  clearPriceTimer() {
    if (this._priceTimer) {
      clearTimeout(this._priceTimer);
      this._priceTimer = null;
    }
  }

  /**
   * Тело запроса цены — или null, если спрашивать нечего: у режима нет ключа
   * площадки, калькулятор не показан или в составе нет ни одного действия.
   *
   * Калькуляторы заводятся сразу для всех режимов всех площадок, а показан
   * один: без проверки на текущий открытие любой страницы стоило бы 35
   * запросов цены. Пока идёт конструирование, currentCalculator ещё не
   * выбран — первый запрос уходит из ModeSelection, когда страница режима
   * смонтирована.
   */
  buildPricePayload() {
    const marketKey = this.mode.marketKey;
    if (!marketKey)
      return null;

    if (this.store.currentCalculator !== this)
      return null;

    const activities = this.buildActivities();
    if (!activities.length)
      return null;

    return {
      marketKey,
      activities,
      days: this.deadlines[this.store.selectedDeadlineIndex ?? 0]?.days,
      range: this.sliderRange,
      useCommunalServer: this.useCommunalServer,
      testing: this.store.testing,
    };
  }

  /**
   * Спросить цену у сервера. Ответы приходят не в порядке отправки, поэтому
   * применяется только последний запрошенный — иначе цена «прыгает» назад на
   * запоздавшем ответе от предыдущего положения ползунка.
   */
  requestServerPrice(payload) {
    this._priceTimer = null;

    // За время паузы могли переключить режим: платить запросом за калькулятор,
    // которого уже не видно, незачем.
    if (this.store.currentCalculator !== this) {
      this.isPriceLoading = false;
      return;
    }

    this._priceLastRequestAt = Date.now();

    const seq = this._priceRequestSeq + 1;
    this._priceRequestSeq = seq;
    this.isPriceLoading = true;

    fetchPriceWithCache(payload)
      .then(res => {
        runInAction(() => {
          if (this._priceRequestSeq !== seq)
            return;
          this.applyServerPricing(res);
          this.isPriceLoading = false;
          this.priceError = false;
        });
      })
      .catch(() => {
        runInAction(() => {
          if (this._priceRequestSeq !== seq)
            return;
          // Цену показать нечего: молча оставить прежнюю нельзя — по ней
          // человек решает, создавать ли проект, а спишется всё равно
          // серверная. Гасим цену и говорим об этом вслух.
          this.isPriceLoading = false;
          this.priceError = true;
          this.price = 0;
          this.priceMin = 0;
          this.priceMax = 0;
        });
      });
  }

  /**
   * Цена показана и ей можно верить. Пока её нет, создавать проект нельзя:
   * иначе человек нажимает «Создать» под нулём, а списывается настоящая сумма.
   */
  get isPriceReady() {
    return this.store.testing || (!this.priceError && this.price > 0);
  }

  /** @param {{price: number, breakdown: object, pricing: object, bounds: object}} res */
  applyServerPricing(res) {
    this.pricing = res.pricing ?? null;
    this.priceMin = res.bounds?.min ?? 0;
    this.priceMax = res.bounds?.max ?? 0;

    // У ботов аренда фермы показывается отдельной строкой, поэтому в price
    // остаётся только стоимость самих ботов.
    if (this.mode.bots) {
      this.serverPrice = res.breakdown?.server ?? 0;
      this.price = res.breakdown?.chains ?? 0;
    } else {
      this.price = res.price;
    }
  }
}

/**
 * Класс с общей логикой для работы с площадками.
 * @description Класс позволяет настроить и создать на сервере новый проект.
 * На данный момент представляет собой сборную солянку
 * настроек проекта для всех возможных площадок.
 * @property {Calculator[]} calculators Доступные калькуляторы.
 * Между калькуляторами можно переключаться и настраивать их
 * независимо друг от друга.
 * Калькулятор определяет виды и количество действий,
 * которые можно заказывать на площадке.
 * На выходе калькулятор выдает цену и сроки,
 * на которые должен согласиться пользователь.
 */
export default class MarketStore {

  market;
  calculators;

  constructor(market) {
    this.market = market;

    this.calculators = market.modes.map(mode => makeAutoObservable(mode.calculator ?? new Calculator(this, market, mode)));
    this.hasBots = market.modes.some(mode => mode.bots);

    this.defaultSelectedCalculatorIndex = (() => {
      let pathname = document.location.pathname;
      while (pathname.endsWith('/'))
        pathname = pathname.substring(0, pathname.length - 1);
      if (pathname.endsWith('-new'))
        pathname = pathname.substring(0, pathname.length - '-new'.length);
      const path = pathname ?? '/';

      const index = this.calculators.findIndex(calculator => calculator.mode.path === path);
      return index >= 0 ? index : 0;
    })();
    this.selectedCalculatorIndex = this.defaultSelectedCalculatorIndex;
    this.currentCalculator = this.calculators[this.defaultSelectedCalculatorIndex]

    this.handleInitialReviewCount();
    makeAutoObservable(this);
  }

  get needFullParams() {
    if (!this.market.isMarketplace)
      return false;
    const calculator = this.currentCalculator;
    return !!(calculator.chainsCount || calculator.reviewsCount);
  }

  handleInitialReviewCount() {
    const urlParams = new URLSearchParams(window.location.search);
    const initialReviewCount = urlParams.get('number-reviews');

    if (initialReviewCount) {
      const reviewCount = parseInt(decodeURIComponent(atob(initialReviewCount, 10)));
      if (!isNaN(reviewCount) && reviewCount > 0) {
        const reviewAction = this.currentCalculator.actions.find(action => action.type === 'MAIN.REVIEW');
        if (reviewAction) {
          this.currentCalculator.setReviewsCount(reviewCount);
        }
      }
    }
  }

  linkToAd = '';
  linkError = false;
  setLinkToAd(link) {
    this.linkToAd = link.trim();
    this.linkError = false;
  }

  aiQuestions = [];
  aiQuestionError = '';

  addAiQuestion(value) {
    const trimmed = value.trim();
    if (trimmed && !this.aiQuestions.includes(trimmed)) {
      this.aiQuestions.push(trimmed);
      this.aiQuestionError = '';
    }
  }

  removeAiQuestion(value) {
    this.aiQuestions = this.aiQuestions.filter(q => q !== value);
  }

  isFreeOnlineService = false;
  freeOnlineServiceError = false;
  setIsFreeOnlineService(boolean) {
    this.isFreeOnlineService = boolean;
    this.freeOnlineServiceError = false;
  }

  dosage = '';
  setDosage(value) {
    this.dosage = value;
  }

  address = '';
  setAddress(address) {
    this.address = address;
  }

  country = '';
  setCountry(country) {
    this.country = country;
  }

  city = '';
  cityError = false;
  setCity(city) {
    this.city = city;
    this.cityError = false;
  }

  allCitiesFlag = false;
  setAllCitiesFlag(flag = true) {
    this.allCitiesFlag = flag;
  }

  currentCategoryIndex = undefined;
  categoryError = false;
  selectCategoryByIndex(index) {
    this.currentCategoryIndex = index;
    this.categoryError = false;
  }

  trackTitle = '';
  trackTitleError = false;
  setTrackTitle(title) {
    this.trackTitle = title;
    this.trackTitleError = false;
  }

  albumTitle = '';
  albumTitleError = false;
  setAlbumTitle(title) {
    this.albumTitle = title;
    this.albumTitleError = false;
  }

  musician = '';
  musicianError = false;
  setMusician(name) {
    this.musician = name;
    this.musicianError = false;
  }

  searchPhrases = [];
  searchPhrasesError = '';

  longInterests = [];

  brandPhrases = [];
  brandPhrasesError = '';

  keysClusterBuffer = '';
  setKeysClusterBuffer(buffer) {
    this.keysClusterBuffer = buffer;
  }

  longInterestsBuffer = '';
  setLongInterestsBuffer(buffer) {
    this.longInterestsBuffer = buffer;
  }

  addSearchPhrase(phraseToAdd) {
    phraseToAdd = phraseToAdd.trim();
    if (!phraseToAdd)
      return;

    if (this.searchPhrases.find(phrase => phrase === phraseToAdd))
      return;

    this.searchPhrases.push(phraseToAdd);
    this.searchPhrasesError = '';
  }

  removeSearchPhrase(phraseToRemove) {
    this.searchPhrases = this.searchPhrases.filter(phrase => phrase !== phraseToRemove);
    this.searchPhrasesError = '';
  }

  addBrandPhrase(phrase) {
    if (!this.brandPhrases.includes(phrase) && this.brandPhrases.length < 10) {
      this.brandPhrases.push(phrase);
      this.brandPhrasesError = '';
    }
  }

  removeBrandPhrase(phrase) {
    this.brandPhrases = this.brandPhrases.filter(p => p !== phrase);
    this.brandPhrasesError = '';
  }

  // TODO: Split by customizer models
  reviewComment = '';
  reviewCommentError = false;
  setReviewComment(text) {
    this.reviewComment = text;
    if (text.length > 549) {
      this.reviewCommentError = true;
    } else {
      this.reviewCommentError = false;
    }
  }
  commentComment = '';
  commentCommentError = false;
  setCommentComment(text) {
    this.commentComment = text;
    if (text.length > 449) {
      this.commentCommentError = true;
    } else {
      this.commentCommentError = false;
    }
  }

  selectedOnlineService = null;
  customOnlineService = '';
  customOnlineServiceError = false;

  setSelectedOnlineService(service) {
    if (
      service?.title !== "Другое (укажите свою услугу)" &&
      service?.title !== "Своя услуга"
    ) {
      this.customOnlineService = '';
    }
    this.selectedOnlineService = service;
    this.customOnlineServiceError = false;
  }

  setCustomOnlineService(text) {
    this.customOnlineService = text;
    this.customOnlineServiceError = false;
  }

  reviewWarranty = false;
  setReviewWarranty(flag) {
    if (flag === this.reviewWarranty)
      return;
    this.reviewWarranty = flag;
    for (const calculator of this.calculators)
      calculator.recalculatePriceActionsDays();
  }

  selectedDeadlineIndex = 1;
  testing = false;
  selectDeadline(i) {
    this.selectedDeadlineIndex = i;
    this.testing = (this.currentCalculator.mode.bots && (this.selectedDeadlineIndex === 0));

    if (this.testing && this.isBotFarm) {
      this.setIsBotFarm(false);
      return;
    }

    // Тестовый прогон бесплатный, а срок входит в запрос цены — спрашиваем заново.
    this.currentCalculator.syncServerPrice();
  }

  selectCalculator(i) {
    this.selectedCalculatorIndex = i;
    this.currentCalculator = this.calculators[i];

    this.testing = (this.currentCalculator.mode.bots && (this.selectedDeadlineIndex === 0));

    // TODO: it's very bad code
    if (this.currentCalculator.mode.bots) {
      if (this.selectedDeadlineIndex > 1)
        this.selectDeadline(1);
    }

    this.currentCalculator.syncServerPrice();
  }

  isActivitiesCalculatorManual = false;
  setIsActivitiesCalculatorManual(flag) {
    this.isActivitiesCalculatorManual = flag;
  }

  instructionsScreen = false;
  setInstructionsScreen(value) {
    this.instructionsScreen = value;
  }
  projectSetup = false;
  setProjectSetup(value) {
    this.projectSetup = value;
  }

  windowsOS = true;
  setSelectedOS(os) {
    this.windowsOS = os === 'Windows';
  }

  pfYandex = true; // TODO: change to Common SEO, not only Yandex
  setPfYandex(url) {
    this.pfYandex = url === 'yandex';
  }

  isProgrevSsylok = false;
  setIsProgrevSsylok(value) {
    this.isProgrevSsylok = value;
  }

  isTogglerActive = true;
  setTogglerActive(value) {
    this.isTogglerActive = value;
    if (!this.isTogglerActive) {
      this.brandPhrasesError = '';
    }
  }

  executorType = 'real';
  setExecutorType(type) {
    this.executorType = type;
    if (type === 'bots')
      this.selectCalculator(this.calculators.findIndex(calculator => calculator.mode.bots));
  }

  isBotFarm = false;
  setIsBotFarm(value) {
    this.isBotFarm = value;
    for (const calculator of this.calculators)
      calculator.onSetBotIsFarm?.(value);
  }

  screenshotComment = '';
  screenshotCommentError = false;
  setScreenshotComment(text) {
    this.screenshotComment = text;
    this.screenshotCommentError = false;
  }

  isPhotosUpload = false;
  setPhotosUpload(value) {
    this.isPhotosUpload = value;
  }

  photosUrl = '';
  setPhotosUrl(text) {
    this.photosUrl = text;
  }

  simulateFreqs = false; // по умолчанию "Равномерно"
  setSimulateFreqs(value) {
    this.simulateFreqs = value;
  }

  // AI Agent selection for SeoGeoAi market
  selectedAiAgent = null;
  setSelectedAiAgent(agent) {
    this.selectedAiAgent = agent;
  }

  validateStep1() {
    const needFullParams = this.needFullParams;

    const showError = (error, inputName) => {
      messageBoxStore.showError(error);
      if (inputName === 'link') this.linkError = true;
      else if (inputName === 'category') this.categoryError = true;
      else if (inputName === 'city') this.cityError = true;
      else if (inputName === 'freeOnlineService') this.freeOnlineServiceError = true;
      else if (inputName === 'trackTitle') this.trackTitleError = true;
      else if (inputName === 'albumTitle') this.albumTitleError = true;
      else if (inputName === 'musician') this.musicianError = true;
      return false;
    };

    if (needFullParams) {
      if (!AppStore.uploadedFiles.default) {
        AppStore.uploadedFileError = true;
        messageBoxStore.showError('Прикрепите скриншот объявления');
        return;
      }
    }

    if (this.market.isMusicPlatform) {
      if (!this.trackTitle.trim())
        return showError('Укажите название трека', 'trackTitle');

      if (!this.albumTitle.trim())
        return showError('Укажите название альбома', 'albumTitle');

      if (!this.musician.trim())
        return showError('Укажите имя исполнителя', 'musician');

      if (!this.linkToAd.trim())
        return showError('Укажите ссылку на трек', 'link');
    }

    if (!this.market.isPharmacy && !this.market.isMusicPlatform) {
      const skipHostValidation = !this.pfYandex && this.market.isSeo;
      const linkError = this.market.validateTargetUrl(this.linkToAd, skipHostValidation);
      if (linkError)
        return showError(linkError, 'link');

      if (this.market.id === 'avito' && this.currentCalculator.mode.id === "reviews" && !this.isFreeOnlineService) {
        return showError('Подтвердите, что в объявлении есть бесплатная онлайн-услуга', 'freeOnlineService');
      }
    }

    if (needFullParams) {
      if (this.currentCategoryIndex === undefined && !this.market.isPharmacy)
        return showError('Укажите категорию объявления', 'category');

      if (!this.address)
        return showError('Укажите город продвижения', 'city');
      if (!this.country)
        return showError('Адрес продвижения должен быть со страной', 'city');
      if (!this.city)
        return showError('Адрес продвижения должен быть с городом', 'city');

      if (!this.searchPhrases.length) {
        this.searchPhrasesError = 'Добавьте поисковую фразу';
        return false;
      }
    }

    if (this.market.hasSearchPhrase && !this.searchPhrases.length) {
      this.searchPhrasesError = 'Добавьте название для поиска';
      return false;
    }

    this.linkError = false;
    this.categoryError = false;
    this.cityError = false;
    this.freeOnlineServiceError = false;
    this.trackTitleError = false;
    this.albumTitleError = false;
    this.musicianError = false;

    return true;
  }

  validateStep2() {
    // Без свежей серверной цены дальше не пускаем: иначе человек уходит на
    // следующий шаг, видя ноль, а спишется настоящая сумма.
    if (!this.currentCalculator.isPriceReady) {
      messageBoxStore.showError(
        this.currentCalculator.priceError
          ? 'Не удалось получить цену. Обновите страницу и попробуйте ещё раз'
          : 'Считаем цену, подождите пару секунд'
      );
      return false;
    }

    if (this.market.isMusicPlatform)
      this.linkError = false;

    const reviewAction = this.currentCalculator.actions.find(action => action.type === 'MAIN.REVIEW');
    if (reviewAction?.count) {
      let serviceError = false;
      let serviceErrorMsg = '';

      if (this.selectedOnlineService) {
        if (
          this.selectedOnlineService.title === "Другое (укажите свою услугу)" ||
          this.selectedOnlineService.title === "Своя услуга"
        ) {
          if (!this.customOnlineService.trim()) {
            serviceError = true;
            serviceErrorMsg = 'Заполните поле для своей услуги';
          }
        }
      }

      if (serviceError) {
        messageBoxStore.showError(serviceErrorMsg);
        this.customOnlineServiceError = true;
        return false;
      }

      if (!this.reviewComment.trim()) {
        messageBoxStore.showError('Укажите требования к отзыву');
        this.reviewCommentError = true;
        return false;
      }
    }
    this.reviewCommentError = false;

    const commentAction = this.currentCalculator.actions.find(action => action.type === 'MAIN.COMMENT');
    if (commentAction?.count) {
      if (!this.commentComment.trim()) {
        messageBoxStore.showError('Укажите требования к комментариям');
        this.commentCommentError = true;
        return false;
      }
    }
    this.commentCommentError = false;

    return true;
  }

  validateStepSeo() {
    const showError = (error, inputName) => {
      messageBoxStore.showError(error);
      if (inputName === 'link') this.linkError = true;
      else if (inputName === 'city') this.cityError = true;
      return false;
    };

    if (!!this.selectedAiAgent) {
      if (!this.aiQuestions.length) {
        this.aiQuestionError = 'Добавьте хотя бы один вопрос для AI';
        return false;
      }
    }
    else if (this.pfYandex) {
      if (!this.searchPhrases.length) {
        this.searchPhrasesError = 'Добавьте поисковую фразу';
        return false;
      }

      if (this.isTogglerActive && !this.brandPhrases.length) {
        this.brandPhrasesError = 'Добавьте бренд-фразу';
        return false;
      }

      if (!AppStore.getUploadedFile('landingScreenshot')) {
        AppStore.uploadedFileError = true;
        messageBoxStore.showError('Прикрепите скриншот целевой страницы');
        return;
      }
    } else {
      if (!this.linkToAd)
        return showError("Укажите корректную ссылку на объявление", 'link');

      if (!AppStore.getUploadedFile('linkScreenshot')) {
        AppStore.uploadedFileError = true;
        messageBoxStore.showError('Прикрепите скриншот ссылки');
        return;
      }

      if (!this.isProgrevSsylok && !AppStore.getUploadedFile('landingScreenshot')) {
        AppStore.uploadedFileError = true;
        messageBoxStore.showError('Прикрепите скриншот целевой страницы');
        return;
      }
    }

    this.linkError = false;
    this.cityError = false;

    return true;
  }

  validateStepSeoBots() {
    const showError = (error, inputName) => {
      messageBoxStore.showError(error);
      if (inputName === 'link') this.linkError = true;
      else if (inputName === 'city') this.cityError = true;
      else if (inputName === 'searchPhrases') this.searchPhrasesError = true;
      return false;
    };

    const linkError = this.market.validateTargetUrl(this.linkToAd);
    if (linkError)
      return showError(linkError, 'link');

    if (!this.allCitiesFlag) {
      if (!this.address)
        return showError('Укажите город продвижения', 'city');
      if (!this.country)
        return showError('Адрес продвижения должен быть со страной', 'city');
      if (!this.city)
        return showError('Адрес продвижения должен быть с городом', 'city');
    }

    this.searchPhrases = this.keysClusterBuffer.split(',').map(key => key.trim()).filter(key => key);
    if (!this.searchPhrases.length)
      return showError('Укажите кластер ключевых запросов', 'searchPhrases');
    this.longInterests = this.longInterestsBuffer.split(',').map(key => key.trim()).filter(key => key);

    this.linkError = false;
    this.cityError = false;
    this.searchPhrasesError = false;

    return true;
  }

  isCreatingProject = false;
  /** @param {boolean} flag */
  setIsCreatingProject(flag = true) {
    this.isCreatingProject = flag;
  }

  agreedRulesCreatingProject = false;
  /** @param {boolean} flag */
  setAgreedRulesCreatingProject(flag = true) {
    this.agreedRulesCreatingProject = flag;
  }

  isCreatingNewProject = false;
  async createNewProject(captchaToken) {
    if (this.isCreatingNewProject || !this.agreedRulesCreatingProject)
      return;
    this.isCreatingNewProject = true;

    const calculator = this.currentCalculator;

    let projectId;
    try {
      // Цену и прайс проекта ставит сервер по marketKey — отсюда уходит
      // только состав. Единственное, что мы решаем про деньги, это выбор
      // сервера для ботов: он меняет цену, но остаётся выбором пользователя.
      const pricing = calculator.mode.bots && calculator.useCommunalServer
        ? { useCommunalServer: true }
        : undefined;

      /** @type {import('client.js').ProjectParams} */
      let project = {
        type: (this.market.backendId || this.market.id).toUpperCase(),
        marketKey: calculator.mode.marketKey,
        activities: this.testing ? null : calculator.actions.reduce((activities, action) => {
          const type = action.type;
          let a = {
            type: type,
            count: action.count,
          };
          if (type === 'MAIN.REVIEW') {
            a.info = this.reviewComment;
            if (this.reviewWarranty)
              a.warranty = true;
          } else if (type === 'MAIN.COMMENT') {
            a.info = this.commentComment;
          }
          activities.push(a);
          return activities;
        }, []),
        days: calculator.deadlines[this.selectedDeadlineIndex].days,
        ...(pricing ? { pricing } : {}),
      };

      if (calculator.mode.bots) {
        project.bots = true;

        if (this.simulateFreqs) {
          if (!project.extras)
            project.extras = {};
          project.extras.simulateFreqs = true;
        }
      }

      if (this.testing)
        project.testing = true;

      if (this.selectedOnlineService) {
        project.extras = project.extras || {};
        const isCustomService = this.selectedOnlineService.title === "Другое (укажите свою услугу)" || this.selectedOnlineService.title === "Своя услуга";

        if (isCustomService && this.customOnlineService.trim()) {
          if (this.customOnlineService.trim()) {
            project.extras.customService = this.customOnlineService.trim();
          }
        } else {
          project.extras.selectedService = {
            title: this.selectedOnlineService.title,
            description: this.selectedOnlineService.text
          };
        }
      }

      // add comment
      if (this.reviewComment || this.commentComment) {
        if (!project.extras)
          project.extras = {};
        project.extras.comment = this.reviewComment || this.commentComment;
      }

      // add photos upload
      if (this.isPhotosUpload) {
        if (!project.extras)
          project.extras = {};
        project.extras.photosUrl = this.photosUrl;
      }

      if (this.market.isMusicPlatform) {
        if (!project.extras)
          project.extras = {};
        const extras = project.extras;
        extras.trackTitle = this.trackTitle;
        extras.albumTitle = this.albumTitle;
        extras.musician = this.musician;
      }

      if (!this.market.isPharmacy && !this.market.isSeo && !this.market.isMaps) {
        const link = this.linkToAd;
        const appendHttps = !(link.startsWith('https://') || link.startsWith('http://'));
        project.link = appendHttps ? ('https://' + link) : link;
      }

      if (this.needFullParams) { // TODO: isMarketplace
        project = {
          ...project,
          screenshot: await AppStore.computeScreenshotBlob(AppStore.uploadedFiles.default),
          address: this.address,
          country: this.country,
          city: this.city,
          searchPhrases: this.searchPhrases,
        }

        if (!this.market.isPharmacy)
          project.category = this.market.categories[this.currentCategoryIndex].type;
      }

      if (this.market.hasSearchPhrase) {
        project.searchPhrases = this.searchPhrases;
      }

      if (this.market.appMarket) {
        if (!project.extras)
          project.extras = {};
        project.extras.model = this.market.appMarket;
      }

      if (this.market.isPharmacy && this.dosage) {
        if (!project.extras)
          project.extras = {};
        project.extras.dosage = this.dosage;
      }

      if (this.longInterests.length)
        project.longInterests = this.longInterests;

      if (this.selectedAiAgent && this.market.aiAgents && this.market.aiAgents.length > 0) {
        if (!project.extras)
          project.extras = {};
        project.extras.model = this.selectedAiAgent.id;
        project.searchPhrases = this.aiQuestions;
      }
      else if (this.market.isSeo || this.market.isMaps) { // TODO: class MarketSeoStore extends MarketStore
        if (this.pfYandex) {
          project = {
            ...project,
            link: calculator.mode.bots ? ((this.linkToAd.startsWith('https://') || this.linkToAd.startsWith('http://')) ? this.linkToAd : ('https://' + this.linkToAd)) : undefined,
            searchPhrases: this.searchPhrases,
            address: this.allCitiesFlag ? undefined : this.address,
            country: this.allCitiesFlag ? undefined : this.country,
            city: this.allCitiesFlag ? true : this.city,
            screenshot: calculator.mode.bots ? undefined : await AppStore.computeScreenshotBlob(AppStore.uploadedFiles.landingScreenshot),
          };
          if (this.isTogglerActive && this.brandPhrases.length)
            project.brandPhrases = this.brandPhrases;
        } else {
          project = {
            ...project,
            link: (this.linkToAd.startsWith('https://') || this.linkToAd.startsWith('http://')) ? this.linkToAd : ('https://' + this.linkToAd),
            screenshot: await AppStore.computeScreenshotBlob(AppStore.uploadedFiles.linkScreenshot), // TODO: подождать поддержку на беке
            ...(AppStore.uploadedFiles.landingScreenshot && {
              screenshot: await AppStore.computeScreenshotBlob(AppStore.uploadedFiles.landingScreenshot),
            })
          };
          if (this.screenshotComment)
            project.screenshotComment = this.screenshotComment;
        }
      }

      projectId = await client.createProject(project, captchaToken);
    } catch (e) {
      if (e instanceof ApiError)
        messageBoxStore.showError(e.message ?? 'Не удалось создать проект. Пожалуйста, повторите попытку');
      else
        throw e;
    } finally {
      this.isCreatingNewProject = false;
    }

    return projectId;
  }

  clearFieldsAfterCreateProjects() {
    this.setDosage('');
    this.setLinkToAd('');
    this.setIsFreeOnlineService(false);
    this.selectCategoryByIndex();
    this.setAddress('');
    this.setCountry('');
    this.setCity('');
    this.setTrackTitle('');
    this.setAlbumTitle('');
    this.setMusician('');
    this.searchPhrases = [];
    this.longInterests = [];
    this.brandPhrases = [];
    this.keysClusterBuffer = '';
    this.longInterestsBuffer = '';
    this.setReviewComment('');
    this.setCommentComment('');
    this.setScreenshotComment('');
    this.setPhotosUrl('');
    this.setSelectedOnlineService(null);
    this.setCustomOnlineService('');
    AppStore.setLegalConfirm(false);
    AppStore.setAdultConfirm(false);
    AppStore.setPoliticalConfirm(false);
    AppStore.clearUploadedFile('default');
    AppStore.clearUploadedFile('linkScreenshot');
    AppStore.clearUploadedFile('landingScreenshot');
    this.setSimulateFreqs(false);
    this.setSelectedAiAgent(null);
  }
}
